import React, { useState } from "react";
import { useDropzone } from "react-dropzone";
import { FileDropzone } from "@design-system/FileDropzone/FileDropzone";
import { Button } from "@design-system/Button/Button";
import { UploadIcon } from "@icons";
import { VStack } from "@design-system/VStack/VStack";
import { BooleanControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import gridStyles from "./playgroundControls.module.scss";
import styles from "./SpecializedInputPlayground.module.scss";

export const FileDropzonePlayground = () => {
    const [fileNames, setFileNames] = useState<string[]>([]);
    const [isDragActiveForced, setIsDragActiveForced] = useState(false);
    const [hasCover, setHasCover] = useState(true);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop: (acceptedFiles: File[]) => {
            setFileNames(acceptedFiles.map((file) => file.name));
        },
    });

    const isDropTargetActive = isDragActive || isDragActiveForced;

    return (
        <SandboxLayout
            controls={
                <>
                    <section className={styles.fields}>
                        <h2>State</h2>
                        <BooleanControl
                            label="Force isDragActive"
                            checked={isDragActiveForced}
                            onChange={setIsDragActiveForced}
                        />
                        <BooleanControl
                            label="Render cover"
                            checked={hasCover}
                            onChange={setHasCover}
                        />
                        <p className={styles.hint}>
                            The cover is mounted whenever <code>cover</code> is
                            anything other than <code>null</code>, but it only
                            becomes visible while <code>isDragActive</code> is
                            true. Passing <code>null</code> removes the element
                            entirely.
                        </p>
                    </section>
                    <section className={styles.fields}>
                        <h2>Try it</h2>
                        <p className={styles.hint}>
                            Drag files onto the target or click it to browse.
                            Nothing is uploaded — the showcase only keeps the
                            dropped file names.
                        </p>
                        <Button
                            type="button"
                            variant="primary"
                            size="md"
                            onClick={() => setFileNames([])}
                        >
                            Clear selected files
                        </Button>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={
                    <>
                        {isDropTargetActive ? "drag active" : "idle"} ·{" "}
                        {fileNames.length} files
                    </>
                }
                output={JSON.stringify(
                    {
                        componentProps: {
                            rootProps: "useDropzone().getRootProps()",
                            isDragActive: isDropTargetActive,
                            cover: hasCover ? "node" : null,
                            coverClassName: "showcase class",
                            className: "showcase class",
                            "data-testid": "playground-file-dropzone",
                        },
                        showcaseState: {
                            isDragActiveFromDropzone: isDragActive,
                            isDragActiveForced,
                            fileNames,
                        },
                    },
                    null,
                    2,
                )}
            >
                <VStack className={gridStyles.fillWidth} gap={16}>
                    <FileDropzone
                        rootProps={getRootProps()}
                        isDragActive={isDropTargetActive}
                        cover={
                            hasCover ? (
                                <>
                                    <UploadIcon />
                                    <span>Drop files to attach</span>
                                </>
                            ) : null
                        }
                        coverClassName={styles.dropzoneCover}
                        className={styles.dropzoneSurface}
                        data-testid="playground-file-dropzone"
                    >
                        <input {...getInputProps()} />
                        <div className={styles.dropzoneBody}>
                            <UploadIcon />
                            <p>Drag documents here, or click to browse</p>
                            {fileNames.length > 0 ? (
                                <ul className={styles.fileList}>
                                    {fileNames.map((fileName) => (
                                        <li key={fileName}>{fileName}</li>
                                    ))}
                                </ul>
                            ) : null}
                        </div>
                    </FileDropzone>
                    <p className={styles.hint}>
                        The dashed target and the cover background are local
                        showcase CSS passed through the real{" "}
                        <code>className</code> and <code>coverClassName</code>{" "}
                        props — the design system only positions the cover and
                        toggles its visibility.
                    </p>
                </VStack>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
